import { createFileRoute } from '@tanstack/react-router'

import { JellyIcon } from '@/components/icons'
import { BriefEmpty, BriefList, Composer } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import { BRIEF_GROUPS, BRIEF_SHOOT, BRIEF_SUGGESTIONS } from '@/mocks/klient/brief'

/** Before anyone has written into it — the state a fresh booking starts in. */
type BriefSearch = { stav?: 'prazdny' }

export const Route = createFileRoute('/klient/foceni/brief')({
  validateSearch: (search: Record<string, unknown>): BriefSearch =>
    search.stav === 'prazdny' ? { stav: 'prazdny' } : {},
  head: () => ({ meta: [{ title: 'Co nafotíme · Aplikace Bitez' }] }),
  component: KlientBrief,
})

/**
 * The shoot's brief (spec §7.2) — the one screen in the app **two people write
 * and a third one reads**: the client says what they want to sell, Bára what
 * the content plan needs, and Tomáš opens it before he sets off.
 *
 * **Not a thread** (owner, 2026-08-18): a wall of messages would leave the
 * photographer translating a conversation into a list in the one moment he has
 * none. Every line is a thing to photograph, grouped the way a venue is shot —
 * food, the room, the people — and discussion stays in the chat, which spec
 * §7.2 already gives all three of them.
 *
 * It still borrows the chat's composer rather than a form with a Save button,
 * because a brief is not filled in once: it grows in the days before the shoot,
 * one thought at a time, usually from a phone. The suggestions are what a gastro
 * venue asks for over and over, so the common case is a single tap.
 */
function KlientBrief() {
  const { stav } = Route.useSearch()
  const empty = stav === 'prazdny'

  return (
    <div className="flex min-h-full flex-col px-4 pt-4 pb-2">
      <Link
        href="/klient/foceni"
        className="-ml-1 flex w-fit items-center gap-1.5 rounded-full px-1 py-1 text-[10px] font-bold tracking-widest text-ink/40 uppercase transition-colors duration-150 hover:text-ink/70"
      >
        <JellyIcon name="arrow-left" className="size-3" />
        Focení
      </Link>

      <header className="mt-3">
        <h1 className="font-display text-2xl leading-[0.9] uppercase">Co nafotíme</h1>
        <p className="mt-1.5 text-[11px] font-semibold text-ink/45">
          Focení {BRIEF_SHOOT.date} v {BRIEF_SHOOT.time}
        </p>
      </header>

      {/* Why bother writing anything — as a person, not a paragraph. */}
      <div className="mt-4 flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-sm">
        <img
          src={BRIEF_SHOOT.photographer.avatar}
          alt=""
          width={80}
          height={80}
          className="size-9 shrink-0 rounded-full object-cover"
        />
        <p className="min-w-0 flex-1 text-[11px] leading-relaxed text-ink/60">
          <span className="font-bold text-ink">{BRIEF_SHOOT.photographer.name}</span> si soupisku
          přečte, než k vám vyrazí. Píšete do ní vy i Bára.
        </p>
      </div>

      <div className="animate-rise-up mt-7 flex-1">
        {empty ? <BriefEmpty /> : <BriefList groups={BRIEF_GROUPS} />}
      </div>

      <Composer
        suggestions={BRIEF_SUGGESTIONS}
        placeholder="Co chcete nafotit?"
        attachLabel="Přiložit fotku pro inspiraci"
      />
    </div>
  )
}
